import { emailSchema, loginSchema } from '@locaveri/shared'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { LoaderCircle, ShieldCheck } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { useForm } from 'react-hook-form'

import { AuthLayout } from '@/components/auth/auth-layout'
import { BotoesDeLoginSocial } from '@/components/auth/botoes-de-login-social'
import { CampoDeSenha } from '@/components/auth/campo-de-senha'
import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ApiError } from '@/lib/api'
import { entrar, ehDesafioDe2fa, ehSetupDe2faObrigatorio } from '@/features/auth/api'
import { aplicarErrosDaApi, validarCom } from '@/lib/formulario'
import { destinoInternoSeguro, useParametroDaBusca } from '@/lib/rota'
import { useAuthStore } from '@/stores/auth-store'

/**
 * Entrada no painel.
 *
 * ---------------------------------------------------------------------------
 * O QUE A MENSAGEM DE ERRO PODE DIZER
 * ---------------------------------------------------------------------------
 * "E-mail não cadastrado" entrega a lista de usuários da locadora para quem
 * tiver paciência de tentar endereços. Por isso credencial errada é sempre a
 * mesma frase, sem distinguir e-mail de senha — a distinção é feita pela API só
 * onde ela não vaza nada (e-mail não verificado, conta bloqueada, que são
 * estados de uma conta que o usuário comprovadamente conhece).
 */

interface FormularioDeLogin {
  email: string
  senha: string
}

const CAMPOS = ['email', 'senha'] as const

export function LoginPage(): ReactElement {
  const navigate = useNavigate()
  const definirSessao = useAuthStore((estado) => estado.definirSessao)
  const definirDesafio = useAuthStore((estado) => estado.definirDesafio)
  const definirSetupPendente = useAuthStore((estado) => estado.definirSetupPendente)
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const [codigoDoErro, setCodigoDoErro] = useState<string | null>(null)

  const destino = destinoInternoSeguro(useParametroDaBusca('redirecionar'))

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormularioDeLogin>({
    defaultValues: { email: '', senha: '' },
  })

  const login = useMutation({
    mutationFn: entrar,
    onSuccess: (resposta) => {
      if (ehDesafioDe2fa(resposta)) {
        definirDesafio(resposta)
        void navigate({ to: '/2fa' })
        return
      }

      // Primeiro login de papel administrativo: ainda não há sessão — o
      // desafio autoriza só o setup do TOTP, e a confirmação devolve a sessão.
      if (ehSetupDe2faObrigatorio(resposta)) {
        definirSetupPendente(resposta)
        void navigate({ to: '/2fa/configurar' })
        return
      }

      definirSessao(resposta)

      // Quem ainda precisa configurar o segundo fator não vai para o painel: o
      // guard da rota protegida devolveria para cá em seguida.
      void navigate({ to: resposta.usuario.precisaConfigurarTotp ? '/2fa/configurar' : destino })
    },
    onError: (erro: unknown) => {
      setCodigoDoErro(ApiError.is(erro) ? erro.code : null)
      setErroGeral(aplicarErrosDaApi(erro, setError, CAMPOS))
    },
  })

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)
    setCodigoDoErro(null)

    const entrada = { email: valores.email, senha: valores.senha }
    const validado = loginSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral('Confira o e-mail e a senha antes de continuar.')
      return
    }

    login.mutate(entrada)
  })

  const ocupado = login.isPending || isSubmitting

  return (
    <AuthLayout
      titulo="Entrar no painel"
      descricao="Acesso restrito à equipe da operação. Locação de veículos blindados."
      rodape={
        <span className="flex items-center justify-center gap-1.5 text-xs text-fg-muted">
          <ShieldCheck className="size-3.5" aria-hidden="true" />
          Sessões da equipe exigem verificação em duas etapas.
        </span>
      }
    >
      <form
        noValidate
        onSubmit={(evento) => {
          void aoEnviar(evento)
        }}
        className="space-y-4"
      >
        {erroGeral === null ? null : (
          <Alert variant="danger">
            <AlertDescription>{erroGeral}</AlertDescription>
            {codigoDoErro === 'CONTA_TEMPORARIAMENTE_BLOQUEADA' ? (
              <AlertDescription>
                O bloqueio é temporário e cresce a cada tentativa errada. Se você não lembra a
                senha, use a redefinição em vez de tentar de novo.
              </AlertDescription>
            ) : null}
          </Alert>
        )}

        <Campo rotulo="E-mail" obrigatorio erro={errors.email?.message}>
          {(campo) => (
            <Input
              {...campo}
              {...register('email', {
                required: 'informe o e-mail',
                validate: validarCom(emailSchema),
              })}
              type="email"
              autoComplete="username"
              placeholder="voce@locaveri.com.br"
              disabled={ocupado}
            />
          )}
        </Campo>

        <Campo rotulo="Senha" obrigatorio erro={errors.senha?.message}>
          {(campo) => (
            <CampoDeSenha
              {...campo}
              {...register('senha', { required: 'informe a senha' })}
              autoComplete="current-password"
              disabled={ocupado}
            />
          )}
        </Campo>

        <div className="flex justify-end">
          <Link
            to="/esqueci-senha"
            className="text-brand text-sm underline-offset-4 hover:underline"
          >
            Esqueci minha senha
          </Link>
        </div>

        <Button type="submit" className="w-full" disabled={ocupado}>
          {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
          {ocupado ? 'Entrando…' : 'Entrar'}
        </Button>
      </form>

      <BotoesDeLoginSocial desabilitado={ocupado} />
    </AuthLayout>
  )
}
